import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  Divider,
  LinearProgress,
} from "@mui/material";
import { Add as AddIcon, Delete as DeleteIcon } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import type { CoachingPackage } from "./types";
import { formatHoursDisplay } from "../../../util/formatHours";

interface CoachingPackageSectionProps {
  packages: CoachingPackage[];
  completedHours: number;
  onAddPackage: (pkg: Omit<CoachingPackage, "id">) => void;
  onDeletePackage: (id: string) => void;
}

const CoachingPackageSection: React.FC<CoachingPackageSectionProps> = ({
  packages,
  completedHours,
  onAddPackage,
  onDeletePackage,
}) => {
  const { t } = useTranslation();
  const [isAdding, setIsAdding] = useState(false);
  const [errors, setErrors] = useState<{ startDate?: boolean; endDate?: boolean }>({});
  const [newPackage, setNewPackage] = useState({
    startDate: "",
    endDate: "",
    hoursBooked: "",
    comment: "",
  });

  const handleAdd = () => {
    const newErrors: { startDate?: boolean; endDate?: boolean } = {};
    if (!newPackage.startDate) newErrors.startDate = true;
    if (!newPackage.endDate) newErrors.endDate = true;
    if (newErrors.startDate || newErrors.endDate) {
      setErrors(newErrors);
      return;
    }
    if (newPackage.hoursBooked) {
      onAddPackage({
        startDate: new Date(newPackage.startDate),
        endDate: new Date(newPackage.endDate),
        hoursBooked: Number(newPackage.hoursBooked),
        comment: newPackage.comment || undefined,
      });
      setNewPackage({
        startDate: "",
        endDate: "",
        hoursBooked: "",
        comment: "",
      });
      setErrors({});
      setIsAdding(false);
    }
  };

  const totalHoursBooked = packages.reduce(
    (sum, pkg) => sum + pkg.hoursBooked,
    0,
  );
  const totalHoursUsed = completedHours ?? 0;
  const totalHoursRemaining = totalHoursBooked - totalHoursUsed;

  return (
    <Paper sx={{ p: 2.5, bgcolor: "#fff", borderRadius: 2 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            color: "text.secondary",
            mb: 0,
            textTransform: "uppercase"
          }}>
          {t("coachingNavigator.clientOverview.coachingPackages")}
        </Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setIsAdding(!isAdding)}
        >
          {t("coachingNavigator.clientOverview.addPackage")}
        </Button>
      </Box>
      {/* Summary totals */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 2,
          textAlign: "center",
          py: 1.5,
          px: 2,
          bgcolor: "#f3f3f3",
          borderRadius: 2,
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="h5" sx={{
            fontWeight: 700
          }}>
            {formatHoursDisplay(totalHoursBooked)}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              textTransform: "uppercase"
            }}>
            {t("coachingNavigator.clientOverview.totalBooked")}
          </Typography>
        </Box>
        <Box>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: "primary.main"
            }}>
            {formatHoursDisplay(totalHoursUsed)}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              textTransform: "uppercase"
            }}>
            {t("coachingNavigator.clientOverview.totalUsed")}
          </Typography>
        </Box>
        <Box>
          <Typography
            variant="h5"
            color={
              totalHoursRemaining > 0
                ? "success.main"
                : totalHoursRemaining < 0
                  ? "error.main"
                  : "text.secondary"
            }
            sx={{
              fontWeight: 700
            }}
          >
            {formatHoursDisplay(totalHoursRemaining)}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              textTransform: "uppercase"
            }}>
            {t("coachingNavigator.clientOverview.totalRemaining")}
          </Typography>
        </Box>
      </Box>
      <Divider sx={{ my: 1.5 }} />
      {/* Add form */}
      {isAdding && (
        <Box
          sx={{
            mb: 2,
            p: 1.5,
            bgcolor: "#ffffff",
            borderRadius: 2,
            border: 1,
            borderColor: "divider",
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 1,
              mb: 1,
            }}
          >
            <TextField
              label={t("coachingNavigator.clientOverview.startDate")}
              type="date"
              value={newPackage.startDate}
              onChange={(e) => {
                setNewPackage({ ...newPackage, startDate: e.target.value });
                if (e.target.value) setErrors((prev) => ({ ...prev, startDate: false }));
              }}
              slotProps={{ inputLabel: { shrink: true } }}
              size="small"
              required
              error={errors.startDate}
              helperText={errors.startDate ? t("coachingNavigator.clientOverview.dateRequired") : ""}
            />
            <TextField
              label={t("coachingNavigator.clientOverview.endDate")}
              type="date"
              value={newPackage.endDate}
              onChange={(e) => {
                setNewPackage({ ...newPackage, endDate: e.target.value });
                if (e.target.value) setErrors((prev) => ({ ...prev, endDate: false }));
              }}
              slotProps={{ inputLabel: { shrink: true } }}
              size="small"
              required
              error={errors.endDate}
              helperText={errors.endDate ? t("coachingNavigator.clientOverview.dateRequired") : ""}
            />
          </Box>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 1,
              mb: 1,
            }}
          >
            <TextField
              label={t("coachingNavigator.clientOverview.hoursBooked")}
              type="number"
              value={newPackage.hoursBooked}
              onChange={(e) =>
                setNewPackage({ ...newPackage, hoursBooked: e.target.value })
              }
              size="small"
            />
            <TextField
              label={t("coachingNavigator.clientOverview.comment")}
              value={newPackage.comment}
              helperText={`${newPackage.comment.length}/100`}
              onChange={(e) =>
                setNewPackage({ ...newPackage, comment: e.target.value })
              }
              size="small" slotProps={{ htmlInput: { maxLength: 100 } }}
            />
          </Box>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button
              size="small"
              onClick={() => setIsAdding(false)}
              variant="outlined"
            >
              {t("coachingNavigator.clientOverview.cancel")}
            </Button>
            <Button size="small" variant="contained" onClick={handleAdd}>
              {t("coachingNavigator.clientOverview.add")}
            </Button>
          </Box>
        </Box>
      )}
      {/* Package list */}
      <Box
        sx={{
          maxHeight: 180,
          overflow: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 1,
        }}
      >
        {packages.length === 0 ? (
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              fontStyle: "italic",
              textAlign: "center",
              py: 2
            }}>
            {t("coachingNavigator.clientOverview.noCoachingPackagesYet")}
          </Typography>
        ) : (
          packages.map((pkg) => {
            const progressPercent =
              totalHoursBooked > 0
                ? (totalHoursUsed / totalHoursBooked) * 100
                : 0;

            return (
              <Box
                key={pkg.id}
                sx={{
                  p: 1.5,
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 2,
                  bgcolor: "background.paper",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    mb: 1,
                  }}
                >
                  <Typography variant="caption" sx={{
                    color: "text.secondary"
                  }}>
                    {dayjs(pkg.startDate).format("YYYY-MM-DD")} —{" "}
                    {dayjs(pkg.endDate).format("YYYY-MM-DD")}
                  </Typography>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => onDeletePackage(pkg.id)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(2, 1fr)",
                    gap: 1,
                    textAlign: "center",
                    mb: pkg.comment ? 1 : 0,
                  }}
                >
                  <Box>
                    <Typography variant="h6" sx={{
                      fontWeight: 600
                    }}>
                      {formatHoursDisplay(pkg.hoursBooked)}
                    </Typography>
                    <Typography variant="caption" sx={{
                      color: "text.secondary"
                    }}>
                      {t("coachingNavigator.clientOverview.booked")}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        color: "text.secondary"
                      }}>
                      {pkg.comment && (
                        <Typography
                          variant="body2"
                          sx={{
                            color: "text.secondary",
                            mt: 0.5
                          }}>
                          {pkg.comment}
                        </Typography>
                      )}
                    </Typography>
                    <Typography variant="caption" sx={{
                      color: "text.secondary"
                    }}>
                      {t("coachingNavigator.clientOverview.comment")}
                    </Typography>
                  </Box>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(progressPercent, 100)}
                  sx={{ mt: 1, height: 6, borderRadius: 3 }}
                />
              </Box>
            );
          })
        )}
      </Box>
    </Paper>
  );
};

export default CoachingPackageSection;
